// src/lib/AuditDrawer.js
// One record's life story: a slide-over timeline of its audit-diary entries.
// Superadmin only (the /audit route enforces it server-side too).
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, Clock } from 'lucide-react';
import { API_BASE } from '../apiBase';

export const fmtAuditVal = (field, v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return new Date(v).toLocaleDateString();
  if (typeof v === 'number' && ['total', 'subtotal', 'amount'].includes(field)) return `$${v.toLocaleString()}`;
  return String(v);
};

const ACTION_TONE = {
  create: 'bg-emerald-500/15 text-emerald-300',
  update: 'bg-indigo-500/15 text-indigo-300',
  delete: 'bg-red-500/15 text-red-300',
};

export default function AuditDrawer({ entityType, entityId, label, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    axios.get(`${API_BASE}/audit?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`,
      { headers: { Authorization: localStorage.getItem('token') } })
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]));
  }, [entityType, entityId]);

  return createPortal(
    <div className="fixed inset-0 z-[80] flex justify-end">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-[440px] max-w-full h-full bg-gray-950 border-l border-gray-800 shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-gray-950/95 backdrop-blur border-b border-gray-800/70 px-5 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold flex items-center gap-2"><Clock className="w-4 h-4 text-indigo-400" /> Audit</h2>
            <p className="text-xs text-zinc-500 mt-0.5">{entityType} {label}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-800 text-zinc-400"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          {rows === null && <p className="text-zinc-600 text-sm">Loading…</p>}
          {rows !== null && rows.length === 0 && (
            <p className="text-zinc-600 text-sm">No history recorded for this record yet — the diary started when the audit trail shipped, so older actions predate it.</p>
          )}
          {(rows || []).map(r => (
            <div key={r._id} className="bg-gray-900 border border-gray-800/60 rounded-xl p-3.5">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${ACTION_TONE[r.action] || 'bg-zinc-700/40 text-zinc-300'}`}>{r.action}</span>
                <span className="text-[11px] text-zinc-500">{new Date(r.at).toLocaleString()}</span>
              </div>
              <p className="text-xs text-zinc-400 mb-1.5">
                by <span className="text-zinc-200 font-semibold">{r.actor?.name || r.actor?.email || 'Unknown user'}</span>
                {r.actorRole ? <span className="text-zinc-600"> · {r.actorRole}</span> : null}
              </p>
              {(r.changes || []).map((c, i) => (
                <p key={i} className="text-xs text-zinc-300">
                  <span className="text-zinc-500">{c.field}:</span>{' '}
                  {r.action === 'create'
                    ? fmtAuditVal(c.field, c.to)
                    : <>{fmtAuditVal(c.field, c.from)} <span className="text-zinc-600">→</span> {fmtAuditVal(c.field, c.to)}</>}
                </p>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}
